import { doc, getDoc, increment, writeBatch, type DocumentReference } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Two PowerPoint uploads per person per calendar month (Singapore time),
 * PowerPoint can only be uploaded as a progress update, so that is what it counts.
 *
 * The rules are what enforce it (pptUploads in firestore.rules); this side checks first so people get a plain explanation
 * instead of a failed upload.
 */
export const PPT_MONTHLY_LIMIT = 2;

/** Same key the rules build: `{uid}_{year}-{month}`, month not zero-padded. */
function monthKey(uid: string, now = new Date()): string {
  const sgt = new Date(now.getTime() + 8 * 3_600_000);
  return `${uid}_${sgt.getUTCFullYear()}-${sgt.getUTCMonth() + 1}`;
}

const counterRef = (uid: string) => doc(db, 'pptUploads', monthKey(uid));

function nextMonthName(now = new Date()): string {
  const sgt = new Date(now.getTime() + 8 * 3_600_000);
  const first = new Date(Date.UTC(sgt.getUTCFullYear(), sgt.getUTCMonth() + 1, 1));
  return first.toLocaleDateString(undefined, { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

export class PptLimitError extends Error {
  readonly code = 'ppt-limit';
  constructor() {
    super(
      `You’ve already uploaded ${PPT_MONTHLY_LIMIT} progress updates this month, which is the monthly limit. ` +
        `You can upload more from ${nextMonthName()}.`,
    );
  }
}

export async function pptUsedThisMonth(uid: string): Promise<number> {
  const snap = await getDoc(counterRef(uid)).catch(() => null);
  return Number(snap?.data()?.count ?? 0);
}

/** Throws PptLimitError when this person has no PowerPoint uploads left this month. */
export async function assertPptAllowed(uid: string): Promise<void> {
  if ((await pptUsedThisMonth(uid)) >= PPT_MONTHLY_LIMIT) throw new PptLimitError();
}

/**
 * Save a PowerPoint's record together with this month's count, in one write:
 * the rules accept the record only alongside the +1, and the +1 only up to the
 * limit.
 */
export async function savePptRecord(uid: string, record: DocumentReference, data: Record<string, unknown>) {
  const batch = writeBatch(db);
  batch.set(counterRef(uid), { uid, count: increment(1) }, { merge: true });
  batch.set(record, data);
  try {
    await batch.commit();
  } catch (e) {
    if ((await pptUsedThisMonth(uid)) >= PPT_MONTHLY_LIMIT) throw new PptLimitError();
    throw e;
  }
}

/** A refused upload of a deck usually means the month's count is used up. */
export async function explainPptRefusal(uid: string, e: unknown): Promise<unknown> {
  const code = (e as { code?: string })?.code;
  if (code === 'storage/unauthorized' && (await pptUsedThisMonth(uid)) >= PPT_MONTHLY_LIMIT) {
    return new PptLimitError();
  }
  return e;
}
